import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "totalPoints", "remainingPoints", "finalStat"]
  static values = {
    priority: Number,
    heritageModifications: Object,
    magicModifications: Object
  }

  connect() {
    this.initializeInputs()
    this.updatePoints()
    // Update pools and final stats on initial load
    this.updatePools()
    this.updateFinalStats()
  }

  initializeInputs() {
    this.inputTargets.forEach(input => {
      input.addEventListener('change', (e) => this.handleAttributeChange(e))
    })
  }

  updatePoints() {
    const totalUsed = this.calculateTotalPointsUsed()
    const totalAvailable = this.priorityValue
    const remaining = totalAvailable - totalUsed
    
    this.totalPointsTarget.textContent = totalAvailable
    this.remainingPointsTarget.textContent = remaining
    
    this.syncSubmitState()
  }

  poolValue(primary, halfAttribute, quarterAttribute) {
    return primary + Math.floor(halfAttribute / 2) + Math.floor(quarterAttribute / 4)
  }

  calculateAttributeCost(value) {
    if (value <= 1) return 0 // Base value is free
    if (value <= 10) return value - 1 // 1 point per level from 2-10
    if (value <= 15) return 9 + (value - 10) * 2 // 2 points per level from 11-15
    return 19 + (value - 15) * 3 // 3 points per level from 16-20
  }

  handleAttributeChange(event) {
    const input = event.target
    const newValue = parseInt(input.value, 10) || 1
    const oldValue = parseInt(input.dataset.lastValue || "1", 10)
    
    // Calculate current total points used by other attributes
    let otherAttributesTotal = 0
    this.inputTargets.forEach(attrInput => {
      if (attrInput !== input) {
        const value = parseInt(attrInput.value, 10) || 1
        otherAttributesTotal += this.calculateAttributeCost(value)
      }
    })

    // Calculate maximum points available for this attribute
    const availablePoints = this.priorityValue - otherAttributesTotal
    
    // Find the maximum attribute value we can afford
    let maxAllowedValue = 1 // Start with base value
    for (let testValue = 2; testValue <= 20; testValue++) {
      const cost = this.calculateAttributeCost(testValue)
      if (cost <= availablePoints) {
        maxAllowedValue = testValue
      } else {
        break
      }
    }
    
    // Get the maximum value for this attribute from the input's data attribute
    const maxAttributeValue = parseInt(input.dataset.maxValue, 10) || 20
    
    // Cap the value at the maximum allowed
    const cappedValue = Math.min(newValue, maxAllowedValue, maxAttributeValue)
    
    // Set the capped value
    input.value = cappedValue

    // Store the new value as the last valid value
    input.dataset.lastValue = cappedValue
    
    // Update displays
    this.updatePoints()

    // Update pools in the skills section
    this.updatePools()
    
    // Update final stats
    this.updateFinalStats()
  }

  calculateTotalPointsUsed() {
    return this.inputTargets.reduce((sum, input) => {
      const value = parseInt(input.value, 10) || 1
      return sum + this.calculateAttributeCost(value)
    }, 0)
  }

  incrementAttribute(event) {
    const attributeName = event.target.closest('button').dataset.attribute
    const input = document.querySelector(`input[name="character[${attributeName}]"]`)
    if (input) {
      const currentValue = parseInt(input.value, 10) || 1
      const maxValue = parseInt(input.dataset.maxValue, 10) || 20
      const newValue = Math.min(currentValue + 1, maxValue)
      input.value = newValue
      
      // Trigger both change and input events to update calculations
      const changeEvent = new Event('change', { bubbles: true })
      const inputEvent = new Event('input', { bubbles: true })
      input.dispatchEvent(changeEvent)
      input.dispatchEvent(inputEvent)
    }
  }

  decrementAttribute(event) {
    const attributeName = event.target.closest('button').dataset.attribute
    const input = document.querySelector(`input[name="character[${attributeName}]"]`)
    if (input) {
      const currentValue = parseInt(input.value, 10) || 1
      const newValue = Math.max(currentValue - 1, 1)
      input.value = newValue
      
      // Trigger both change and input events to update calculations
      const changeEvent = new Event('change', { bubbles: true })
      const inputEvent = new Event('input', { bubbles: true })
      input.dispatchEvent(changeEvent)
      input.dispatchEvent(inputEvent)
    }
  }

  updatePools() {
    const getAttributeValue = (name) => {
      const input = document.querySelector(`input[name="character[${name}]"]`)
      return parseInt(input.value, 10) || 0
    }

    // Get base attribute values
    const baseStr = getAttributeValue('strength')
    const baseBody = getAttributeValue('body')
    const baseRea = getAttributeValue('reaction')
    const baseInt = getAttributeValue('intelligence')
    const baseWil = getAttributeValue('willpower')
    const baseCha = getAttributeValue('charisma')

    // Apply creation-time modifications to get effective values
    const heritageMods = this.heritageModificationsValue || {}
    const magicMods = this.magicModificationsValue || {}
    const str = baseStr + (heritageMods.strength || 0) + (magicMods.strength || 0)
    const body = baseBody + (heritageMods.body || 0) + (magicMods.body || 0)
    const rea = baseRea + (heritageMods.reaction || 0) + (magicMods.reaction || 0)
    const int = baseInt + (heritageMods.intelligence || 0) + (magicMods.intelligence || 0)
    const wil = baseWil + (heritageMods.willpower || 0) + (magicMods.willpower || 0)
    const cha = baseCha + (heritageMods.charisma || 0) + (magicMods.charisma || 0)

    const pools = {
      brawn: this.poolValue(str, body, wil),
      finesse: this.poolValue(rea, body, int),
      focus: this.poolValue(int, rea, wil),
      resolve: wil + Math.floor(int / 2) + Math.floor(cha / 2)
    }

    Object.entries(pools).forEach(([pool, value]) => {
      const element = document.querySelector(`#${pool}_pool`)
      if (element) {
        element.textContent = value
      }
    })
    
    // Dispatch event to notify skill selection controller that pools have been updated
    const poolsUpdatedEvent = new CustomEvent('poolsUpdated', { bubbles: true })
    this.element.dispatchEvent(poolsUpdatedEvent)
  }

  updateFinalStats() {
    const getAttributeValue = (name) => {
      const input = this.element.querySelector(`input[name="character[${name}]"]`)
      return parseInt(input.value, 10) || 0
    }

    // Get base attribute values
    const baseStr = getAttributeValue('strength')
    const baseBody = getAttributeValue('body')
    const baseRea = getAttributeValue('reaction')
    const baseInt = getAttributeValue('intelligence')
    const baseWil = getAttributeValue('willpower')
    const baseCha = getAttributeValue('charisma')

    // Apply creation-time modifications to get effective values
    const heritageMods = this.heritageModificationsValue || {}
    const magicMods = this.magicModificationsValue || {}
    const effectiveValues = {
      strength: baseStr + (heritageMods.strength || 0) + (magicMods.strength || 0),
      body: baseBody + (heritageMods.body || 0) + (magicMods.body || 0),
      reaction: baseRea + (heritageMods.reaction || 0) + (magicMods.reaction || 0),
      intelligence: baseInt + (heritageMods.intelligence || 0) + (magicMods.intelligence || 0),
      willpower: baseWil + (heritageMods.willpower || 0) + (magicMods.willpower || 0),
      charisma: baseCha + (heritageMods.charisma || 0) + (magicMods.charisma || 0)
    }

    // Update each final stat display
    this.finalStatTargets.forEach(statElement => {
      const attributeName = statElement.getAttribute('data-attribute-name')
      const baseValue = getAttributeValue(attributeName)
      const effectiveValue = effectiveValues[attributeName]

      // Find the value span within this stat element
      const valueSpan = statElement.querySelector('.final-stat-value')
      if (valueSpan) {
        valueSpan.textContent = effectiveValue
      }

      // Show/hide the final stat based on whether it differs from base
      if (baseValue !== effectiveValue) {
        statElement.style.display = ''
      } else {
        statElement.style.display = 'none'
      }
    })
  }

  syncSubmitState() {
    const submitButton = this.element.closest('form')?.querySelector('button[type="submit"]')
    if (!submitButton) return

    const remainingTargets = document.querySelectorAll(
      '[data-attribute-selection-target="remainingPoints"], [data-skill-selection-target="remainingPoints"], [data-knowledge-selection-target="remainingPoints"], [data-etiquette-selection-target="remainingPoints"]'
    )
    const hasNegativeRemaining = Array.from(remainingTargets).some((target) => {
      const match = target.textContent.match(/-?\d+/)
      return match && parseInt(match[0], 10) < 0
    })

    submitButton.disabled = hasNegativeRemaining
    submitButton.classList.toggle('opacity-50', hasNegativeRemaining)
    submitButton.classList.toggle('cursor-not-allowed', hasNegativeRemaining)
  }
}
